import { createPortfolio, getPortfolios } from "@/pages/PortfolioBuilder/api";
import { createTagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import {
  getQueries,
  postQuery,
} from "@/widget-components/newsquawk-headlines/api/headline-query";
import type { Portfolio } from "@/types/Portfolio";
import { emptyNotifications } from "@/types/notifications";
import type { Widget } from "@/utils/widgets";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { buildLayoutExportData } from "../templates/packLayout";
import { applyLayoutData } from "../templates/applyTemplate";
import type { TemplateWidgetSpec } from "../templates/types";
import type { ProvisioningPlan } from "./types";

export type ApplyPlanResult = {
  createdPortfolios: number;
  createdTagWatchlists: number;
  createdQueries: number;
  tabTitle: string | null;
};

/**
 * Executes a ProvisioningPlan with the user's own token, in dependency order:
 * portfolios → tag watchlists → headline queries (building a ref → id map),
 * then the dashboard tab through the same buildLayoutExportData →
 * applyLayoutData pipeline the starter templates use. The backend never
 * writes anything — this is the only write path.
 *
 * Partial-failure model: writes are applied in order and reported per step;
 * a failure mid-way leaves earlier resources in place (visible in the
 * builders, deletable) — acceptable for v1, no rollback machinery.
 */
type RefKind = "ticker" | "keyword" | "tag" | "query";

/** Filter-panel section for a referenced resource, by kind. */
const FILTER_SECTION: Record<RefKind, string> = {
  ticker: "ticker-watchlists-filter",
  keyword: "keyword-watchlists-filter",
  tag: "tag-watchlists-filter",
  query: "category-filter",
};

const HIGHLIGHT_SECTION: Partial<Record<RefKind, string>> = {
  ticker: "ticker-watchlists-highlight",
  keyword: "keyword-watchlists-highlight",
};

export async function applyPlan(
  plan: ProvisioningPlan,
  options: { hasAccessToWidget: (widget: Widget) => boolean }
): Promise<ApplyPlanResult> {
  const refToId = new Map<string, number>();

  // The plan references resources by bare ref; the portal's filter sections
  // are derived from each ref's resource type.
  const refKind = new Map<string, RefKind>();
  for (const p of plan.portfolios) {
    refKind.set(p.ref, p.tickers.length > 0 ? "ticker" : "keyword");
  }
  for (const t of plan.tags_watchlists) refKind.set(t.ref, "tag");
  for (const q of plan.headline_queries) refKind.set(q.ref, "query");

  // "lib:<name>" / "libq:<name>" refs point at NEWSQUAWK prebuilt watchlists
  // and filters — nothing is created for them; they resolve by NAME against
  // the live library (ids differ per environment, names are the stable key).
  // Unresolved names (library drift) drop silently; the rest still applies.
  const libRefs = new Set<string>();
  const libQueryRefs = new Set<string>();
  if (plan.dashboard_tab) {
    for (const widget of plan.dashboard_tab.widgets) {
      for (const ref of [...widget.highlight_refs, ...widget.filter_refs]) {
        if (ref.startsWith("libq:")) libQueryRefs.add(ref);
        else if (ref.startsWith("lib:")) libRefs.add(ref);
      }
    }
  }

  let existing: Portfolio[] = [];
  if (plan.portfolios.length > 0 || libRefs.size > 0) {
    existing = await getPortfolios();
  }
  for (const ref of libRefs) {
    const name = ref.slice(4);
    const match = existing.find(
      (p) => p.owner_type === "NEWSQUAWK" && p.name === name
    );
    if (match) {
      refToId.set(ref, match.id);
      refKind.set(ref, match.tickers.length > 0 ? "ticker" : "keyword");
    }
  }

  if (libQueryRefs.size > 0) {
    const response = await getQueries();
    const queries: { id: number; name: string; owner_type?: string }[] =
      response.data?.items ?? response.data ?? [];
    for (const ref of libQueryRefs) {
      const name = ref.slice(5);
      const match = queries.find(
        (q) => q.owner_type === "NEWSQUAWK" && q.name === name
      );
      if (match && typeof match.id === "number") {
        refToId.set(ref, match.id);
        refKind.set(ref, "query");
      }
    }
  }

  // 1. Portfolios — the pref-storage API expects a client-allocated id
  //    (mirrors useWatchlistCreator's convention).
  let createdPortfolios = 0;
  if (plan.portfolios.length > 0) {
    let nextId = Math.max(0, ...existing.map((p: Portfolio) => p.id)) + 1;
    for (const portfolioPlan of plan.portfolios) {
      const portfolio: Portfolio = {
        id: nextId,
        name: portfolioPlan.name,
        tickers: portfolioPlan.tickers,
        keywords: portfolioPlan.keywords,
        color: "#FF0000",
        priority: 1,
        notifications: emptyNotifications(),
        customizations: { includeRelatedHeadlines: false },
        owner_type: "USER",
      };
      await createPortfolio(portfolio);
      refToId.set(portfolioPlan.ref, nextId);
      nextId += 1;
      createdPortfolios += 1;
    }
  }

  // 2. Tag watchlists — server allocates the id.
  let createdTagWatchlists = 0;
  for (const watchlistPlan of plan.tags_watchlists) {
    const response = await createTagsWatchlist({
      name: watchlistPlan.name,
      tags: watchlistPlan.tags,
    });
    const id = response.data?.id;
    if (typeof id === "number") refToId.set(watchlistPlan.ref, id);
    createdTagWatchlists += 1;
  }

  // 3. Headline queries — substitute refs into real ids.
  let createdQueries = 0;
  for (const queryPlan of plan.headline_queries) {
    const query: CategoryQuery = {
      id: undefined,
      queryName: queryPlan.name,
      categories: queryPlan.tags,
      searchKeyword: queryPlan.search_keyword || undefined,
      portfolioIds: queryPlan.portfolio_refs
        .map((ref) => refToId.get(ref))
        .filter((id): id is number => typeof id === "number"),
      tagsWatchlistIds: queryPlan.tags_watchlist_refs
        .map((ref) => refToId.get(ref))
        .filter((id): id is number => typeof id === "number"),
      keywordOp: queryPlan.keyword_op,
      filterMode: queryPlan.filter_op,
      keywordWatchlistOp: "OR",
      notifications: emptyNotifications(),
    };
    const response = await postQuery(query);
    const id = response.data?.id;
    if (typeof id === "number") refToId.set(queryPlan.ref, id);
    createdQueries += 1;
  }

  // 4. Dashboard tab via the shared template pipeline. The plan carries an
  //    ordered widget list only — geometry is packed client-side.
  let tabTitle: string | null = null;
  if (plan.dashboard_tab) {
    const usedSuffixes = new Map<string, number>();
    const specs: TemplateWidgetSpec[] = plan.dashboard_tab.widgets.map(
      (widgetPlan) => {
        // Unique pseudo-composite ids so duplicate widgets remap cleanly.
        const count = usedSuffixes.get(widgetPlan.widget_id) ?? 0;
        usedSuffixes.set(widgetPlan.widget_id, count + 1);
        const id =
          count === 0
            ? `${widgetPlan.widget_id}-a`
            : `${widgetPlan.widget_id}-${String.fromCharCode(97 + count)}`;

        const filter: Record<string, string[]> = {};
        const highlight: Record<string, string[]> = {};

        const addRef = (
          target: Record<string, string[]>,
          sections: Partial<Record<RefKind, string>>,
          ref: string
        ) => {
          const kind = refKind.get(ref);
          const id = refToId.get(ref);
          const section = kind && sections[kind];
          if (!section || id === undefined) return;
          (target[section] ??= []).push(String(id));
        };

        for (const ref of widgetPlan.highlight_refs) {
          addRef(highlight, HIGHLIGHT_SECTION, ref);
        }
        for (const ref of widgetPlan.filter_refs) {
          addRef(filter, FILTER_SECTION, ref);
        }
        if (widgetPlan.asset_class_tags.length > 0) {
          filter["basic-filter"] = widgetPlan.asset_class_tags;
        }

        return {
          id,
          ...(Object.keys(filter).length > 0 && { filter }),
          ...(Object.keys(highlight).length > 0 && { highlight }),
        };
      }
    );

    const data = buildLayoutExportData(plan.dashboard_tab.title, specs);
    const result = applyLayoutData(data, {
      hasAccessToWidget: options.hasAccessToWidget,
      repackOnDrop: true,
    });
    tabTitle = result.tabTitle;
  }

  return { createdPortfolios, createdTagWatchlists, createdQueries, tabTitle };
}
